import { useState } from 'react';
import { useTranslations } from '@/i18n/use-translations.tsx';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import CloseIcon from '@mui/icons-material/Close';
import Widget from '@/components/common/widget.tsx';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { IgnorableClickAwayListener } from '@/components/common/ignorable-click-away-listener.tsx';
import useEventMode from '@/components/events/hooks/use-event-mode';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';
import { useEventCrewWidgetSynced, useFlowCrewWidgetSynced } from './use-crew-widget-synced';
import { AddCrewContributorInput } from './add-crew-contributor.input';
import { CrewList } from './crew-list';
import { EVENT_CREW_ROLES, FLOW_CREW_ROLES } from '@/types/dtos/contributors/response/contributor.dto';
import { useEventStore } from '@/state/stores/use-event-store';
import { usePermissionsQuery } from '@/state/queries/permissions/use-permissions.ts';
import { getObjectId } from '@/helpers/object.id.helper';
import { eventHasPermission, managePermissionForEventCrewRoleName } from '@/utils/event-crew-permissions';

function CrewWidgetEvent({ eventSlotId }: { eventSlotId?: string }) {
  const t = useTranslations();
  const [showAddInput, setShowAddInput] = useState(false);
  const mode = useEventMode();
  const event = useEventStore((s) => s.event);
  const eventIdFromStore = event?.origin ? getObjectId(event.origin) : event?.id;
  const { data: permissions } = usePermissionsQuery();
  const eventPerms = eventIdFromStore ? permissions?.events?.[eventIdFromStore] : undefined;
  const { crewRows, eventId } = useEventCrewWidgetSynced();
  const canAssignAnyEventCrew = EVENT_CREW_ROLES.some((r) =>
    eventHasPermission(eventPerms, managePermissionForEventCrewRoleName(r)),
  );
  const canAdd = EVENT_CREW_ROLES.length > 0 && canAssignAnyEventCrew;

  return (
    <Widget
      data-testid="event-crew"
      title={t('event.detail.crewWidget.title', { count: crewRows.length })}
      primaryButton={
        canAdd
          ? {
              label: showAddInput ? t('common.cancel') : t('common.add'),
              disabled: isOrganizeMode(mode),
              onClick: () => setShowAddInput(!showAddInput),
              startIcon: showAddInput ? <CloseIcon /> : <AddOutlinedIcon />,
              testId: 'event-crew-add-crew-button',
              id: 'add-crew-button',
            }
          : undefined
      }
    >
      {showAddInput && canAdd && eventId && (
        <IgnorableClickAwayListener onClickAway={() => setShowAddInput(false)} ignoreId="add-crew-button">
          <div>
            <AddCrewContributorInput
              variant="event"
              eventId={eventId}
              eventSlotId={eventSlotId}
              onSuccess={() => setShowAddInput(false)}
            />
          </div>
        </IgnorableClickAwayListener>
      )}
      <CrewList rows={crewRows} sx={{ m: '0.4rem 0' }} />
    </Widget>
  );
}

function CrewWidgetFlow({ flow, eventSlotId }: { flow: FlowDto; eventSlotId?: string }) {
  const t = useTranslations();
  const [showAddInput, setShowAddInput] = useState(false);
  const mode = useEventMode();
  const event = useEventStore((s) => s.event);
  const eventIdFromStore = event?.origin ? getObjectId(event.origin) : event?.id;
  const { data: permissions } = usePermissionsQuery();
  const eventPerms = eventIdFromStore ? permissions?.events?.[eventIdFromStore] : undefined;
  const { crewRows, eventId, flowForRoles, flowEntityId } = useFlowCrewWidgetSynced(flow, eventSlotId);

  const assignableEventCrewCount = EVENT_CREW_ROLES.filter((r) =>
    eventHasPermission(eventPerms, managePermissionForEventCrewRoleName(r)),
  ).length;
  const selectableRoleCount =
    event?.multiFlow === false ? assignableEventCrewCount + FLOW_CREW_ROLES.length : FLOW_CREW_ROLES.length;
  const canAdd = selectableRoleCount > 0;

  return (
    <Widget
      data-testid="flow-crew-widget"
      title={t('event.detail.crewWidget.title', { count: crewRows.length })}
      primaryButton={
        canAdd
          ? {
              label: showAddInput ? t('common.cancel') : t('common.add'),
              disabled: isOrganizeMode(mode),
              onClick: () => setShowAddInput(!showAddInput),
              startIcon: showAddInput ? <CloseIcon /> : <AddOutlinedIcon />,
              testId: 'flow-crew-add-crew-button',
              id: 'add-flow-crew-button',
            }
          : undefined
      }
    >
      {showAddInput && canAdd && eventId && (
        <IgnorableClickAwayListener onClickAway={() => setShowAddInput(false)} ignoreId="add-flow-crew-button">
          <div>
            <AddCrewContributorInput
              variant="flow"
              flow={flow}
              flowEntityId={flowEntityId}
              eventId={eventId}
              multiFlow={event?.multiFlow}
              eventSlotId={eventSlotId}
              onSuccess={() => setShowAddInput(false)}
            />
          </div>
        </IgnorableClickAwayListener>
      )}
      <CrewList rows={crewRows} flow={flowForRoles} eventSlotId={eventSlotId} sx={{ m: '0.4rem 0' }} />
    </Widget>
  );
}

type CrewWidgetEventProps = {
  variant: 'event';
  eventSlotId?: string;
};

type CrewWidgetFlowProps = {
  variant: 'flow';
  flow: FlowDto;
  eventSlotId?: string;
};

export const CrewWidget = (props: CrewWidgetEventProps | CrewWidgetFlowProps) => {
  if (props.variant === 'event') {
    return <CrewWidgetEvent eventSlotId={props.eventSlotId} />;
  }
  return <CrewWidgetFlow flow={props.flow} eventSlotId={props.eventSlotId} />;
};
